/**
 * SecurityDashboard Page
 *
 * Fleet-level security overview: tunnel health, traffic, connectivity map,
 * alert feed, and summary metrics.
 *
 * Phase B: Auth monitoring sections (sessions, login activity, MFA) for admin users.
 */

import { useState } from 'react';
import { useSecurityDashboard } from '../hooks/useSecurity';
import { TunnelStatusGrid } from '../components/security/TunnelStatusGrid';
import { TrafficChart } from '../components/security/TrafficChart';
import { NetworkMap } from '../components/security/NetworkMap';
import { SecurityAlertsFeed } from '../components/security/SecurityAlertsFeed';
import { SessionTable } from '../components/security/SessionTable';
import { LoginActivityTable } from '../components/security/LoginActivityTable';
import { MfaStatusGrid } from '../components/security/MfaStatusGrid';
import { SecurityReportModal } from '../components/security/SecurityReportModal';
import { AlertRulesTable } from '../components/security/AlertRulesTable';
import { useSecurityStore } from '../store/securityStore';
import { useAuthStore } from '../store/authStore';
import {
  Shield, Wifi, WifiOff, AlertTriangle, Activity, Loader2,
  Users, KeyRound, Lock, ChevronDown, ChevronUp, ScrollText,
  FileText, Settings,
} from 'lucide-react';
import { Link } from 'react-router-dom';

const ADMIN_ROLES = ['platform_admin', 'parent_org_admin', 'customer_admin'];

function StatCard({ label, value, icon: Icon, color }: {
  label: string;
  value: number | string;
  icon: React.ElementType;
  color?: string;
}) {
  return (
    <div className="bg-ems-bg-card rounded-2xl border border-ems-border p-4">
      <div className="flex items-center justify-between mb-1">
        <Icon className={`w-4 h-4 ${color || 'text-ems-text-muted'}`} />
      </div>
      <p className={`text-2xl font-bold ${color || 'text-ems-text-primary'}`}>{value}</p>
      <p className="text-xs text-ems-text-muted">{label}</p>
    </div>
  );
}

function CollapsibleSection({ title, icon: Icon, children, defaultOpen = false }: {
  title: string;
  icon: React.ElementType;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-ems-bg-card rounded-2xl border border-ems-border">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-4 text-left"
      >
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-ems-green" />
          <h2 className="text-sm font-semibold text-ems-text-primary">{title}</h2>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-ems-text-muted" /> : <ChevronDown className="w-4 h-4 text-ems-text-muted" />}
      </button>
      {open && (
        <div className="px-5 pb-5 pt-0">
          {children}
        </div>
      )}
    </div>
  );
}

export function SecurityDashboard() {
  const { data: dashboard, isLoading } = useSecurityDashboard();
  const { setSelectedSiteId } = useSecurityStore();
  const userRole = useAuthStore((state) => state.user?.role);
  const isAdmin = !!userRole && ADMIN_ROLES.includes(userRole);
  const [reportModalOpen, setReportModalOpen] = useState(false);

  const handleSiteClick = (siteId: string) => {
    setSelectedSiteId(siteId);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-ems-text-muted" />
      </div>
    );
  }

  const fleet = dashboard?.fleet_summary;
  const alerts = fleet?.active_alerts;

  return (
    <div className="space-y-6">
      {/* Fleet Summary Bar */}
      <div className="bg-ems-bg-card rounded-2xl border border-ems-border p-5">
        <div className="flex items-center gap-2 mb-4">
          <Shield className="w-5 h-5 text-ems-green" />
          <h1 className="text-lg font-bold text-ems-text-primary">Security Overview</h1>
          {isAdmin && (
            <div className="ml-auto flex items-center gap-3">
              <button
                onClick={() => setReportModalOpen(true)}
                className="flex items-center gap-1 text-xs text-ems-green hover:text-ems-green/80 transition-colors"
              >
                <FileText className="w-3.5 h-3.5" />
                Export Report
              </button>
              <Link
                to="/security/audit-log"
                className="flex items-center gap-1 text-xs text-ems-green hover:text-ems-green/80 transition-colors"
              >
                <ScrollText className="w-3.5 h-3.5" />
                Audit Log
              </Link>
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-9 gap-3">
          <StatCard
            label="Total Sites"
            value={fleet?.total_sites ?? 0}
            icon={Activity}
          />
          <StatCard
            label="Sites Online"
            value={fleet?.sites_online ?? 0}
            icon={Wifi}
            color="text-ems-green"
          />
          <StatCard
            label="Sites Degraded"
            value={fleet?.sites_degraded ?? 0}
            icon={AlertTriangle}
            color={(fleet?.sites_degraded ?? 0) > 0 ? 'text-ems-amber' : 'text-ems-text-muted'}
          />
          <StatCard
            label="Sites Offline"
            value={fleet?.sites_offline ?? 0}
            icon={WifiOff}
            color={(fleet?.sites_offline ?? 0) > 0 ? 'text-ems-red' : 'text-ems-text-muted'}
          />
          <StatCard
            label="Active Tunnels"
            value={`${fleet?.active_tunnels ?? 0}/${fleet?.total_tunnels ?? 0}`}
            icon={Shield}
            color="text-ems-green"
          />
          <StatCard
            label="Active Alerts"
            value={
              (alerts?.critical ?? 0) + (alerts?.high ?? 0) + (alerts?.medium ?? 0) + (alerts?.low ?? 0)
            }
            icon={AlertTriangle}
            color={
              (alerts?.critical ?? 0) > 0 ? 'text-ems-red' :
              (alerts?.high ?? 0) > 0 ? 'text-ems-amber' :
              'text-ems-text-muted'
            }
          />
          {/* Phase B stats */}
          <StatCard
            label="Active Sessions"
            value={fleet?.active_sessions ?? 0}
            icon={Users}
            color="text-ems-green"
          />
          <StatCard
            label="Failed Logins (24h)"
            value={fleet?.failed_logins_24h ?? 0}
            icon={KeyRound}
            color={(fleet?.failed_logins_24h ?? 0) > 0 ? 'text-ems-amber' : 'text-ems-text-muted'}
          />
          <StatCard
            label="Locked Accounts"
            value={fleet?.locked_accounts ?? 0}
            icon={Lock}
            color={(fleet?.locked_accounts ?? 0) > 0 ? 'text-ems-red' : 'text-ems-text-muted'}
          />
        </div>

        {/* Alert severity breakdown */}
        {alerts && ((alerts.critical > 0) || (alerts.high > 0) || (alerts.medium > 0) || (alerts.low > 0)) && (
          <div className="flex items-center gap-3 mt-3 pt-3 border-t border-ems-border">
            <span className="text-xs text-ems-text-muted">Alerts:</span>
            {alerts.critical > 0 && (
              <span className="px-2 py-0.5 rounded text-xs font-medium bg-ems-red/10 text-ems-red">
                {alerts.critical} Critical
              </span>
            )}
            {alerts.high > 0 && (
              <span className="px-2 py-0.5 rounded text-xs font-medium bg-ems-amber/10 text-ems-amber">
                {alerts.high} High
              </span>
            )}
            {alerts.medium > 0 && (
              <span className="px-2 py-0.5 rounded text-xs font-medium bg-ems-amber/10 text-ems-amber">
                {alerts.medium} Medium
              </span>
            )}
            {alerts.low > 0 && (
              <span className="px-2 py-0.5 rounded text-xs font-medium bg-ems-sky/10 text-ems-sky">
                {alerts.low} Low
              </span>
            )}
          </div>
        )}
      </div>

      {/* Tunnel Status Grid */}
      <TunnelStatusGrid onSiteClick={handleSiteClick} />

      {/* Two-column layout: Traffic + Network Map */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <TrafficChart />
        <NetworkMap />
      </div>

      {/* Security Alerts Feed */}
      <SecurityAlertsFeed />

      {/* Phase B: Auth Monitoring (Admin only) */}
      {isAdmin && (
        <>
          <CollapsibleSection title="Active Sessions" icon={Users} defaultOpen>
            <SessionTable isAdmin={isAdmin} />
          </CollapsibleSection>

          <CollapsibleSection title="Login Activity" icon={KeyRound}>
            <LoginActivityTable />
          </CollapsibleSection>

          <CollapsibleSection title="MFA Enrollment" icon={Shield}>
            <MfaStatusGrid />
          </CollapsibleSection>

          {/* Phase C: Alert Rules Configuration */}
          <CollapsibleSection title="Alert Rules Configuration" icon={Settings}>
            <AlertRulesTable />
          </CollapsibleSection>
        </>
      )}

      {/* Phase C: Report Generation Modal */}
      {isAdmin && (
        <SecurityReportModal
          isOpen={reportModalOpen}
          onClose={() => setReportModalOpen(false)}
        />
      )}
    </div>
  );
}
